import type {
    IDraftDetail,
    IRefinementDiff,
} from "@/api/clause-checklists/checklist-generation.types";
import {
    adminDraftQueryKeys,
    applyRefinement,
} from "@/api/clause-checklists/drafts";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { DashboardContainer } from "@/components/shared/layout/DashboardContainer";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useChecklistGeneration } from "@/hooks/useChecklistGeneration";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Alert, Box, IconButton, Stack, Typography } from "@mui/material";
import { useMountEffect } from "@react-hookz/web";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DiffPreview } from "./components/DiffPreview";
import { DraftReview } from "./components/DraftReview";
import { GenerationEventLog } from "./components/GenerationEventLog";
import { GenerationPrompt } from "./components/GenerationPrompt";

const GENERATION_REFUSED =
    "The checklist couldn't be generated. Check the organization and group, then try again.";
const GENERATION_FAILED = "The checklist couldn't be generated. Try again.";

const GenerateChecklist = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumb();
    const location = useLocation();
    const queryClient = useQueryClient();

    useDocumentTitle("Generate Checklist");
    const urlDraftId =
        (location.state as { draftId?: number } | null)?.draftId ?? null;
    const {
        generate,
        refine,
        reset,
        state,
        eventLog,
        draftId,
        error,
        errorStatus,
        pendingDiff,
        clearPendingDiff,
        lastPrompt,
        lastOrganizationId,
        lastOrganizationGroupId,
    } = useChecklistGeneration();

    useMountEffect(() => {
        setBreadcrumbs([
            { label: "Admin", path: "home" },
            { label: "Checklists", path: "checklists" },
            { label: "Generate" },
        ]);
    });

    const prevStateRef = useRef(state);
    // Incremented after each refinement to force DraftReview remount via key
    const [draftVersion, setDraftVersion] = useState(0);

    // Determine which draft to show: URL param takes precedence, then generation result
    const activeDraftId = urlDraftId ?? draftId;

    const applyMutation = useMutation({
        mutationFn: (filteredDiff: IRefinementDiff) =>
            applyRefinement(activeDraftId!, filteredDiff),
        onSuccess: () => {
            clearPendingDiff();
            if (activeDraftId !== null) {
                queryClient.removeQueries({
                    queryKey: adminDraftQueryKeys.detail(activeDraftId),
                });
            }
            setDraftVersion((v) => v + 1);
            enqueueSnackbar("Changes applied", { variant: "success" });
        },
        onError: (err) => {
            enqueueSnackbar(
                err instanceof Error ? err.message : "Failed to apply changes",
                { variant: "error" }
            );
        },
    });
    // Build topic name map from cached draft data for DiffPreview labels
    const cachedDraft = activeDraftId
        ? queryClient.getQueryData<IDraftDetail>(
              adminDraftQueryKeys.detail(activeDraftId)
          )
        : undefined;
    const topicNames = useMemo(() => {
        const map: Record<number, string> = {};
        cachedDraft?.topics?.forEach((t) => {
            map[t.topic_id] = t.topic_name;
        });
        return map;
    }, [cachedDraft]);

    const showDraft =
        state !== "refining" &&
        (!!urlDraftId || (!!draftId && ["complete", "error"].includes(state)));

    useEffect(() => {
        const prev = prevStateRef.current;
        if (prev === "refining" && state === "error" && error) {
            // Refinement failed — show error snackbar (draft stays visible via showDraft)
            enqueueSnackbar(error, { variant: "error" });
        }
        // Note: we do NOT clear cache on refining→complete because the diff hasn't
        // been applied yet. Cache clear happens in applyMutation.onSuccess instead.
        prevStateRef.current = state;
    }, [state, activeDraftId, queryClient, error]);

    const handleReset = () => {
        if (urlDraftId) {
            navigate("/admin/checklists/generate", {
                replace: true,
                state: {},
            });
        }
        reset();
    };

    return (
        <DashboardContainer>
            <Box sx={{ maxWidth: 900, mx: "auto", width: "100%" }}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Stack direction="row" alignItems="start" spacing={2}>
                        <IconButton
                            onClick={() => navigate("/admin/checklists")}
                            size="small"
                        >
                            <ArrowLeft />
                        </IconButton>

                        <Stack>
                            <Typography variant="h5" fontWeight={600}>
                                AI Checklist Generator
                            </Typography>
                            <Typography variant="body2">
                                Describe the broker's scenario. This will
                                generate it into a tailored checklist.
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Prompt — shown in idle state when not loading a draft from URL */}
                    {state === "idle" && !urlDraftId && (
                        <GenerationPrompt
                            onGenerate={generate}
                            initialPrompt={lastPrompt}
                            initialOrganizationId={
                                lastOrganizationId ?? undefined
                            }
                            initialOrganizationGroupId={
                                lastOrganizationGroupId ?? undefined
                            }
                        />
                    )}

                    {/* Event log — shown during generation/refinement, and kept
                        visible during diff review so user can see agent reasoning */}
                    <GenerationEventLog
                        entries={eventLog}
                        state={state}
                        error={error}
                        forceVisible={!!pendingDiff}
                    />

                    {/* Diff preview — shown after refinement completes with proposed changes */}
                    {pendingDiff && state === "complete" && activeDraftId && (
                        <DiffPreview
                            diff={pendingDiff}
                            topicNames={topicNames}
                            onApply={(filtered) =>
                                applyMutation.mutate(filtered)
                            }
                            onReject={() => {
                                clearPendingDiff();
                                enqueueSnackbar(
                                    "Refinement rejected — draft unchanged",
                                    { variant: "info" }
                                );
                            }}
                            isApplying={applyMutation.isPending}
                        />
                    )}

                    {/* Error retry — shown only when no draft exists (generation error, not refinement error) */}
                    {state === "error" && !activeDraftId && (
                        <Stack spacing={2}>
                            {eventLog.length === 0 && (
                                <Alert severity="error">
                                    {errorStatus !== null &&
                                    errorStatus >= 400 &&
                                    errorStatus < 500
                                        ? GENERATION_REFUSED
                                        : GENERATION_FAILED}
                                </Alert>
                            )}
                            <Stack sx={{ alignItems: "flex-end" }}>
                                <ActionButton onClick={handleReset}>
                                    Try Again
                                </ActionButton>
                            </Stack>
                        </Stack>
                    )}

                    {/* Draft review — shown when generation is complete, refining, or error with draft.
                        Dimmed when pendingDiff exists so user focuses on the DiffPreview. */}
                    {showDraft && activeDraftId && (
                        <Box
                            sx={{
                                opacity: pendingDiff ? 0.5 : 1,
                                pointerEvents: pendingDiff ? "none" : "auto",
                                transition: "opacity 0.2s ease",
                            }}
                        >
                            <DraftReview
                                key={`draft-${activeDraftId}-v${draftVersion}`}
                                draftId={activeDraftId}
                                onReset={handleReset}
                                onRefine={(prompt) =>
                                    refine(activeDraftId, prompt)
                                }
                            />
                        </Box>
                    )}
                </Stack>
            </Box>
        </DashboardContainer>
    );
};

export default GenerateChecklist;
